import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Banner } from '@lumal/ui';
import { btnPrimary, btnSecondary, btnDanger, tbl, formStyle, badge, drawer } from '../../styles';
import { platformIam, iamErrorMessage } from '../../api';
import type { PlatformPermission, PlatformRole } from '../../types';

// RolesPanel (support-console): CRUD COMPLETO de roles de PLATAFORMA (realm
// operations) vía UI — crear, editar (nombre/rank/permisos) y eliminar (archivar)
// cualquier rol, incluidos los de sistema (el seed los siembra como dato mutable).
// El anti-escalación vive en cmo-core; la UI solo lo refleja.
export function RolesPanel({ csrf }: { csrf: string }) {
  const roles = useQuery({ queryKey: ['pf-iam-roles'], queryFn: platformIam.listRoles });
  const perms = useQuery({ queryKey: ['pf-iam-perms'], queryFn: platformIam.listPermissions });

  if (roles.isLoading) return <p>Cargando roles…</p>;
  if (roles.isError) return <Banner variant="error">{iamErrorMessage(roles.error)}</Banner>;

  const items = roles.data?.items ?? [];
  const permItems = perms.data?.items ?? [];

  return (
    <section data-testid="platform-roles-panel" style={{ marginTop: '1rem' }}>
      <p style={{ color: 'var(--lumal-muted)', marginTop: 0 }}>
        Roles de la plataforma (realm operations). Todos son <strong>editables y eliminables</strong>; el
        seed solo los siembra al arranque. Solo puedes otorgar permisos que tú posees y un rank por debajo del
        tuyo. Eliminar un rol con asignaciones activas exige reasignarlas a otro rol.
      </p>

      <CreateRole csrf={csrf} permissions={permItems} />

      <table style={tbl} data-testid="platform-roles-table">
        <thead>
          <tr>
            <th>Codename</th>
            <th>Nombre</th>
            <th>Tipo</th>
            <th>Rank</th>
            <th>Permisos</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={6} style={{ color: 'var(--lumal-muted)' }}>No hay roles definidos.</td>
            </tr>
          ) : (
            items.map((r) => (
              <RoleRow key={r.id} role={r} roles={items} csrf={csrf} permissions={permItems} />
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}

function RoleRow({
  role,
  roles,
  csrf,
  permissions,
}: {
  role: PlatformRole;
  roles: PlatformRole[];
  csrf: string;
  permissions: PlatformPermission[];
}) {
  const [editing, setEditing] = useState(false);
  return (
    <>
      <tr data-testid={`platform-role-${role.codename}`}>
        <td><code style={{ fontSize: '0.78rem' }}>{role.codename}</code></td>
        <td>{role.display_name}</td>
        <td><span style={badge}>{role.system ? 'sistema' : 'custom'}</span></td>
        <td>{role.rank}</td>
        <td title={role.permissions.join(', ')}>{role.permissions.length}</td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <button
            type="button"
            style={btnSecondary}
            onClick={() => setEditing((v) => !v)}
            data-testid={`platform-role-edit-${role.codename}`}
          >
            {editing ? 'Cancelar' : 'Editar'}
          </button>{' '}
          <DeleteRole role={role} roles={roles} csrf={csrf} />
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={6}>
            <EditRole role={role} csrf={csrf} permissions={permissions} onDone={() => setEditing(false)} />
          </td>
        </tr>
      )}
    </>
  );
}

function PermPicker({
  permissions,
  selected,
  setSelected,
}: {
  permissions: PlatformPermission[];
  selected: Set<string>;
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const grouped = useMemo(() => {
    const m = new Map<string, PlatformPermission[]>();
    for (const p of permissions) {
      const arr = m.get(p.module) ?? [];
      arr.push(p);
      m.set(p.module, arr);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [permissions]);

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleModule = (perms: PlatformPermission[]) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = perms.every((p) => next.has(p.permission_key));
      for (const p of perms) {
        if (allOn) next.delete(p.permission_key);
        else next.add(p.permission_key);
      }
      return next;
    });

  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <strong style={{ fontSize: '0.9rem' }}>Permisos ({selected.size} seleccionados)</strong>
      <div data-testid="platform-perm-picker" style={{ marginTop: 8 }}>
        {grouped.map(([mod, perms]) => {
          const allOn = perms.every((p) => selected.has(p.permission_key));
          return (
            <fieldset key={mod} style={{ ...drawer, marginTop: 8 }}>
              <legend style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
                {mod}
                <button
                  type="button"
                  style={{ ...btnSecondary, padding: '0.1rem 0.45rem', fontSize: '0.72rem' }}
                  onClick={() => toggleModule(perms)}
                  data-testid={`platform-perm-module-${mod}`}
                >
                  {allOn ? 'ninguno' : 'todos'}
                </button>
              </legend>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 4 }}>
                {perms.map((p) => (
                  <label
                    key={p.permission_key}
                    style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.82rem' }}
                    title={p.description || p.permission_key}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(p.permission_key)}
                      onChange={() => toggle(p.permission_key)}
                      data-testid={`platform-perm-${p.permission_key}`}
                    />
                    <span>{p.action}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}

function CreateRole({ csrf, permissions }: { csrf: string; permissions: PlatformPermission[] }) {
  const qc = useQueryClient();
  const [codename, setCodename] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [rank, setRank] = useState(10);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const create = useMutation({
    mutationFn: () =>
      platformIam.createRole(csrf, {
        codename: codename.trim(),
        display_name: displayName.trim(),
        rank,
        permissions: [...selected],
      }),
    onSuccess: () => {
      setMsg({ kind: 'ok', text: `Rol "${displayName}" creado.` });
      setCodename('');
      setDisplayName('');
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['pf-iam-roles'] });
    },
    onError: (e) => setMsg({ kind: 'error', text: iamErrorMessage(e) }),
  });

  return (
    <details style={{ marginBottom: 12 }}>
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>+ Crear rol de plataforma</summary>
      <form
        style={formStyle}
        data-testid="platform-create-role-form"
        onSubmit={(e) => {
          e.preventDefault();
          setMsg(null);
          create.mutate();
        }}
      >
        <input
          placeholder="codename (ej. soporte_l2)"
          value={codename}
          required
          onChange={(e) => setCodename(e.target.value)}
          data-testid="platform-role-codename"
        />
        <input
          placeholder="Nombre visible"
          value={displayName}
          required
          onChange={(e) => setDisplayName(e.target.value)}
          data-testid="platform-role-display"
        />
        <input
          type="number"
          placeholder="rank"
          value={rank}
          min={0}
          onChange={(e) => setRank(Number(e.target.value))}
          data-testid="platform-role-rank"
        />
        <PermPicker permissions={permissions} selected={selected} setSelected={setSelected} />
        <button
          type="submit"
          style={{ ...btnPrimary, gridColumn: '1 / -1', justifySelf: 'start' }}
          disabled={create.isPending}
          data-testid="platform-create-role-submit"
        >
          {create.isPending ? 'Creando…' : 'Crear rol'}
        </button>
        {msg && <Banner variant={msg.kind === 'ok' ? 'ok' : 'error'}>{msg.text}</Banner>}
      </form>
    </details>
  );
}

function EditRole({
  role,
  csrf,
  permissions,
  onDone,
}: {
  role: PlatformRole;
  csrf: string;
  permissions: PlatformPermission[];
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [displayName, setDisplayName] = useState(role.display_name);
  const [rank, setRank] = useState(role.rank);
  const [selected, setSelected] = useState<Set<string>>(new Set(role.permissions));
  const [msg, setMsg] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: () =>
      platformIam.updateRole(csrf, role.id, {
        display_name: displayName.trim(),
        rank,
        permissions: [...selected],
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pf-iam-roles'] });
      onDone();
    },
    onError: (e) => setMsg(iamErrorMessage(e)),
  });

  return (
    <form
      style={formStyle}
      data-testid={`platform-edit-role-form-${role.codename}`}
      onSubmit={(e) => {
        e.preventDefault();
        setMsg(null);
        update.mutate();
      }}
    >
      <input
        placeholder="Nombre visible"
        value={displayName}
        required
        onChange={(e) => setDisplayName(e.target.value)}
        data-testid={`platform-edit-role-display-${role.codename}`}
      />
      <input
        type="number"
        value={rank}
        min={0}
        onChange={(e) => setRank(Number(e.target.value))}
        data-testid={`platform-edit-role-rank-${role.codename}`}
      />
      <PermPicker permissions={permissions} selected={selected} setSelected={setSelected} />
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
        <button
          type="submit"
          style={btnPrimary}
          disabled={update.isPending}
          data-testid={`platform-edit-role-submit-${role.codename}`}
        >
          {update.isPending ? 'Guardando…' : 'Guardar cambios'}
        </button>
        <button type="button" style={btnSecondary} onClick={onDone}>Cancelar</button>
      </div>
      {msg && <Banner variant="error">{msg}</Banner>}
    </form>
  );
}

function DeleteRole({ role, roles, csrf }: { role: PlatformRole; roles: PlatformRole[]; csrf: string }) {
  const qc = useQueryClient();
  const [reassign, setReassign] = useState<{ open: boolean; to: string } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: (reassignTo?: string) => platformIam.deleteRole(csrf, role.id, reassignTo),
    onSuccess: () => {
      setReassign(null);
      setMsg(null);
      qc.invalidateQueries({ queryKey: ['pf-iam-roles'] });
    },
    onError: (e) => {
      const err = e as { code?: string };
      if (err?.code === 'ROLE_IN_USE') {
        setReassign({ open: true, to: '' });
        setMsg(null);
      } else {
        setMsg(iamErrorMessage(e));
      }
    },
  });

  const onDelete = () => {
    if (!window.confirm(`¿Eliminar (archivar) el rol "${role.display_name}"?`)) return;
    del.mutate(undefined);
  };

  const candidates = roles.filter((r) => r.id !== role.id);

  if (reassign?.open) {
    return (
      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <select
          value={reassign.to}
          onChange={(e) => setReassign({ open: true, to: e.target.value })}
          data-testid={`platform-role-reassign-select-${role.codename}`}
        >
          <option value="">reasignar a…</option>
          {candidates.map((r) => (
            <option key={r.id} value={r.id}>{r.display_name}</option>
          ))}
        </select>
        <button
          type="button"
          style={btnDanger}
          disabled={!reassign.to || del.isPending}
          onClick={() => del.mutate(reassign.to)}
          data-testid={`platform-role-reassign-confirm-${role.codename}`}
        >
          Reasignar y eliminar
        </button>
        <button type="button" style={btnSecondary} onClick={() => setReassign(null)}>Cancelar</button>
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        style={btnDanger}
        disabled={del.isPending}
        onClick={onDelete}
        data-testid={`platform-role-delete-${role.codename}`}
      >
        Eliminar
      </button>
      {msg && <span style={{ color: 'var(--lumal-danger)', fontSize: '0.72rem', marginLeft: 4 }}>{msg}</span>}
    </>
  );
}
